// Кто есть кто на экране: человек по id, человек по аккаунту, цвет для чужой правки.
import type { Row } from './store/model';

export type Person = Row<'person'>;
export type Profile = Row<'profile'>;

export const GRAY = '#6b7280';

/** Кто ещё в команде: активные по алфавиту — списки выбора, авторы вопросов, состав команд. */
export const activePeople = <P extends Pick<Person, 'name' | 'is_active'>>(people: Iterable<P>): P[] =>
  [...people].filter((p) => p.is_active).sort((a, b) => a.name.localeCompare(b.name));
/** Подсказки по буквам: имя начинается с набранного → слово внутри имени начинается → просто содержит; внутри группы — как пришли (алфавит). */
export function rankByQuery<P extends { name: string }>(people: readonly P[], query: string): P[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const rank = (name: string): number => {
    const n = name.toLowerCase();
    if (n.startsWith(q)) return 0;
    if (n.split(/[\s-]+/).some((w) => w.startsWith(q))) return 1;
    return n.includes(q) ? 2 : 3;
  };
  return people.map((p, i) => ({ p, r: rank(p.name), i })).filter((x) => x.r < 3).sort((a, b) => a.r - b.r || a.i - b.i).map((x) => x.p);
}
/** Кому можно назначать задачи: активные и назначаемые, по алфавиту — исполнители, строки People, отпуска. */
export const assignablePeople = <P extends Pick<Person, 'name' | 'is_active' | 'is_assignable'>>(people: Iterable<P>): P[] =>
  activePeople(people).filter((p) => p.is_assignable);

function personOf(profileId: string | null, profiles: Map<string, Profile>, people: Map<string, Person>): Person | null {
  if (!profileId) return null;
  const p = profiles.get(profileId);
  return p?.person_id ? people.get(p.person_id) ?? null : null;
}

export function actorLabel(profileId: string | null, profiles: Map<string, Profile>, people: Map<string, Person>): { name: string; color: string; avatar: string | null } {
  const person = personOf(profileId, profiles, people);
  const profile = profileId ? profiles.get(profileId) : null;
  // картинка Google живёт у аккаунта, имя и цвет — у человека: у офлайн-автора правки есть оба
  const avatar = profile?.avatar_url ?? null;
  if (person) return { name: person.name, color: person.color, avatar };
  return { name: profile ? profile.name || (profile.email.split('@')[0] ?? profile.email) : 'someone', color: GRAY, avatar };
}

/** Что человек делает сейчас — для тултипа кружка присутствия. */
export const doingLabel = (p: { dragging: string[]; editing: string | null; selected: string[]; screen: string | null }): string =>
  p.dragging.length > 1 ? `dragging ${p.dragging.length} tasks` : p.dragging.length ? 'dragging a task' : p.editing ? 'editing a task'
    : p.selected.length > 1 ? `selected ${p.selected.length} tasks` : p.selected.length ? 'selected a task' : p.screen ? `on ${p.screen}` : '';

function agoLabel(prefix: string, at: number, now: number, overAnHour: string): string {
  const min = Math.max(0, Math.round((now - at) / 60_000));
  return min < 1 ? `${prefix} just now` : min < 60 ? `${prefix} ${min} min ago` : `${prefix} ${overAnHour}`;
}
/** «last seen 12 min ago» — для бледного кружка ушедшего. */
export const lastSeen = (leftAt: number, now = Date.now()): string => agoLabel('last seen', leftAt, now, 'about an hour ago');
/** «synced just now» / «synced 3 min ago» — тултип своего аватара. */
export const syncedLabel = (at: number | null, now = Date.now()): string => (at === null ? 'not synced yet' : agoLabel('synced', at, now, 'over an hour ago'));

export const initials = (name: string): string =>
  name.split(/[\s+@._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

/** Фон бара: один цвет или диагональный градиент для двух-трёх исполнителей. */
export function assigneeBackground(colors: string[]): string {
  if (colors.length === 0) return GRAY;
  if (colors.length === 1) return colors[0]!;
  if (colors.length === 2) return `linear-gradient(135deg, ${colors[0]} 50%, ${colors[1]} 50%)`;
  return `linear-gradient(135deg, ${colors[0]} 33%, ${colors[1]} 33%, ${colors[1]} 66%, ${colors[2]} 66%)`;
}

/** Цвета из строки фона: один цвет или градиент assigneeBackground — для расчёта чернил. */
export const gradientColors = (bg: string): string[] => bg.match(/#[0-9a-f]{3,6}/gi) ?? [GRAY];
